import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppShellCheck - is this `app.js` running inside the `index.html` it was built with? If not, load the page once
// more (2026-09-28).
//
// ⛔ WHY: the page's Content-Security-Policy lives in `index.html` (a static website cannot send headers), and the
// code that must obey it lives in `app.js`. A browser can pair a CACHED `index.html` with a FRESH `app.js` - a tab
// the browser restores (the owner's Edge reloads the WACommander tab that way: history 2026-09-27 14:18Z and
// 2026-09-28 00:44Z), or an old shell open across a deploy. Then one build's code runs under another build's policy.
// That is exactly what drew the owner's contact page with NO CSS: today's `app.js` moves the frame's styles into
// `blob:` stylesheets, and the policy from before 2026-09-27 allowed only 'self' + 'unsafe-inline' - every lifted
// sheet refused (reproduced in real Chrome, `AppFrameHeal` says how). Any other policy change (a new service origin
// in `connect-src`) would break the same way, silently.
//
// ⭐ `Expected` is the EXACT policy this build's `index.html` carries - `AppCsp.test.ts` pins the two together, so a
// policy edit without this constant fails the suite. A different one = a document from another build: ONE normal
// reload (it revalidates the document against the server), at most once per `QuietMs` per tab
// (`sessionStorage`, every access wrapped - storage that throws or is empty still allows exactly one try).
// ⚠ The reload is not the only guard: `AppFrameHeal` puts the frame's styles back through the CSSOM whatever the
// policy says, so a tab that may not reload again still shows a styled page.

/** Where the last reload's time is kept (this tab only). */
export interface IShellMemory {
    Read(): string;
    Write(Value: string): void;
}

export class AppShellCheck {
    /** ⛔ Must equal `index.html`'s `<meta http-equiv="Content-Security-Policy">` content byte for byte (AppCsp.test). */
    public static readonly Expected: string = "default-src 'none'; script-src 'self' 'sha256-Y2By32oukC+MsnP/RFKIwXBoFYM0lZ/TwXfygQp8ukQ=';"
        + " style-src 'self' blob:; img-src 'self' blob: data:; media-src blob:; font-src https://fonts.gstatic.com;"
        + " connect-src 'self' https://wacommander-bridge.blackhill-ae574ee3.swedencentral.azurecontainerapps.io"
        + " https://content.dropboxapi.com https://kerrorrelay-pstt65n2.azurewebsites.net"
        + " https://api.onedrive.com;"
        + " frame-src blob:; base-uri 'none'; form-action 'none'; object-src 'none'";

    /** No second reload within ten minutes in one tab - a server that really serves an older shell must not loop. */
    public static readonly QuietMs: number = 10 * 60 * 1000;

    /** A policy was found and it is not this build's. "" (no meta - a page that is not our shell) is not stale. */
    public static Stale = (Found: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Text: string = nz(Found).trim();
            return Text.length > 0 && Text !== AppShellCheck.Expected;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppShellCheck.Stale");
            return false;
        }
    };

    /** May this tab reload now? True when no reload is remembered within `QuietMs`. */
    public static MayReload = (Remembered: string, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Last: number = parseInt(nz(Remembered), 10);
            if (isNaN(Last) === true) { return true; }
            return NowMs - Last >= AppShellCheck.QuietMs || NowMs < Last;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppShellCheck.MayReload");
            return false;
        }
    };

    /** The whole decision: answers true when the caller must reload now (the time is already remembered). */
    public static ShouldReload = (Found: string, Memory: IShellMemory, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppShellCheck.Stale(Found) === false) { return false; }
            if (AppShellCheck.MayReload(Memory.Read(), NowMs) === false) { return false; }
            Memory.Write(String(NowMs));
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppShellCheck.ShouldReload");
            return false;
        }
    };
}
